import { useEffect, useState } from "react";

export type Phase = "dawn" | "day" | "dusk" | "night";
const PHASES: Phase[] = ["dawn", "day", "dusk", "night"];

/** "07:28 AM" -> minutes after midnight. */
function toMinutes(t: string | undefined, fallback: number) {
  const m = t?.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return fallback;
  let h = Number(m[1]) % 12;
  if (m[3].toUpperCase() === "PM") h += 12;
  return h * 60 + Number(m[2]);
}

/**
 * Dawn runs from 45 min before sunrise to 45 min after, dusk from an hour
 * before sunset to 45 min after. Without weather data, 07:00 and 19:00.
 */
export function phaseAt(date: Date, sunrise?: string, sunset?: string): Phase {
  const now = date.getHours() * 60 + date.getMinutes();
  const rise = toMinutes(sunrise, 7 * 60);
  const set = toMinutes(sunset, 19 * 60);
  if (now >= rise - 45 && now < rise + 45) return "dawn";
  if (now >= rise + 45 && now < set - 60) return "day";
  if (now >= set - 60 && now < set + 45) return "dusk";
  return "night";
}

/** A phase forced with ?theme=night etc., for testing and screenshots. */
function override(): Phase | null {
  const t = new URLSearchParams(window.location.search).get("theme");
  return PHASES.includes(t as Phase) ? (t as Phase) : null;
}

/** The current phase of the day, re-checked every minute. */
export function useDayPhase(sunrise?: string, sunset?: string): Phase {
  const [phase, setPhase] = useState<Phase>(
    () => override() ?? phaseAt(new Date(), sunrise, sunset),
  );
  useEffect(() => {
    const check = () => setPhase(override() ?? phaseAt(new Date(), sunrise, sunset));
    check();
    const id = window.setInterval(check, 60 * 1000);
    return () => window.clearInterval(id);
  }, [sunrise, sunset]);
  return phase;
}
